import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { IGuestRating, RatingType } from '@curbnturf/entities';
import { RatingApiService } from '../backend-api/rating-api.service';
import { AuthService } from '../auth/auth.service';
import { IBooking } from '@cnt-workspace/models';
import { parseBookingDate } from '../shared/iso-date.util';

export interface IHostReviewSubScores {
  cleanliness: number;
  communication: number;
  rulesFollowed: number;
  careOfSite: number;
  punctuality: number;
}

export interface IHostReview {
  id: string;
  bookingId: string;
  /** Real backend numeric guest user id — the reliable lookup key (unlike
   *  `guestEmail`, which the public `GET rating/guest/:id` endpoint doesn't
   *  return, for the same reviewer-PII reasons `ReviewService` documents). */
  guestUserId: number;
  hostEmail: string;
  guestEmail: string;
  guestName: string;
  guestInitials: string;
  rating: number; // 1–5, 2-decimal precision
  text: string;
  subScores: IHostReviewSubScores;
  createdAt: string;
}

/** The backend's real auto-reveal cutoff (`PrivateRatingCommunicationService`'s
 *  21-day cron) — a one-sided review is force-revealed this many days after
 *  checkout even if the other side never rates back. Used for UI countdowns/
 *  deadlines only; the actual gating is entirely server-side. */
export const REVIEW_AUTO_REVEAL_DAYS = 21;

/** Epoch ms when a stay's review window closes (`REVIEW_AUTO_REVEAL_DAYS`
 *  after checkout); NaN when the end date doesn't parse. */
export function reviewWindowEnd(b: Pick<IBooking, 'dates'>): number {
  const end = (parseBookingDate(b.dates.end) ?? new Date(b.dates.end)).getTime();
  return end + REVIEW_AUTO_REVEAL_DAYS * 86_400_000;
}

export const GUEST_SUBSCORE_LABELS: Array<{ key: keyof IHostReviewSubScores; label: string; hint: string }> = [
  { key: 'cleanliness', label: 'Cleanliness', hint: 'Left the site as they found it.' },
  { key: 'communication', label: 'Communication', hint: 'Responsive, clear, on time.' },
  { key: 'rulesFollowed', label: 'Followed rules', hint: 'Honored quiet hours, pet rules, etc.' },
  { key: 'careOfSite', label: 'Care of site', hint: 'Treated the property with respect.' },
  { key: 'punctuality', label: 'Punctuality', hint: 'Arrived and departed when expected.' },
];

/** Average of the five host-review sub-scores. Drives the derived overall. */
export function averageHostSubScores(s: IHostReviewSubScores): number {
  return (s.cleanliness + s.communication + s.rulesFollowed + s.careOfSite + s.punctuality) / 5;
}

/** `IHostReviewSubScores` keys → `RatingType`. `RatingType` is a generic
 *  10-value enum shared by listing and guest ratings, not listing-specific —
 *  `quality`/`checkin` are legitimately reused here for `careOfSite`/
 *  `punctuality` rather than needing dedicated enum values. */
const SUB_SCORE_TOPIC_MAP: Record<keyof IHostReviewSubScores, RatingType> = {
  cleanliness: RatingType.cleanliness,
  communication: RatingType.communication,
  rulesFollowed: RatingType.rules,
  careOfSite: RatingType.quality,
  punctuality: RatingType.checkin,
};
const SUB_SCORE_KEYS = Object.keys(SUB_SCORE_TOPIC_MAP) as (keyof IHostReviewSubScores)[];

export interface ISubmitHostReviewParams {
  guestUserId: number;
  reservationId: number;
  subScores: IHostReviewSubScores;
  text: string;
  hostEmail: string;
  guestEmail: string;
  guestName: string;
  guestInitials: string;
}

/**
 * Real-backed host→guest reviews — talks to curbnturf's `/rating/guest` API,
 * the mirror image of `ReviewService`'s listing-review conversion (same
 * plain, NgRx-free `RatingService`, same "no mock fallback" rule, same
 * hydrate-then-sync-read shape). See `ReviewService`'s class doc for the
 * shared reasoning this file doesn't repeat.
 *
 * The backend's `hidden != true` filter is the only reveal gate — there is
 * no client-side reveal-window approximation anymore.
 */
@Injectable({ providedIn: 'root' })
export class HostReviewService {
  private auth = inject(AuthService);
  private rating = inject(RatingApiService);

  private readonly _reviews$ = new BehaviorSubject<IHostReview[]>([]);
  readonly reviews$: Observable<IHostReview[]> = this._reviews$.asObservable();

  // ---- Hydration (async — real HTTP) --------------------------------------

  async hydrateGuest(guestUserId: number): Promise<void> {
    if (!guestUserId) return;
    const rows = await firstValueFrom(this.rating.fetchGuestRatings(guestUserId));
    const reviews = this.groupRows(rows, guestUserId);
    const others = this._reviews$.value.filter((r) => r.guestUserId !== guestUserId);
    this._reviews$.next([...others, ...reviews]);
  }

  private groupRows(rows: IGuestRating[], guestUserId: number): IHostReview[] {
    const groups = new Map<string, IGuestRating[]>();
    for (const row of rows) {
      const key = `${row.reservationId}:${row.creatorId}`;
      const arr = groups.get(key);
      if (arr) arr.push(row);
      else groups.set(key, [row]);
    }

    const reviews: IHostReview[] = [];
    for (const group of groups.values()) {
      const overall = group.find((r) => r.topic === RatingType.overall);
      if (!overall || !overall.score?.description) continue;

      const fallback = overall.score?.score ?? 0;
      const findScore = (topic: RatingType) => group.find((r) => r.topic === topic)?.score?.score;

      reviews.push({
        id: String(overall.id),
        bookingId: `res-${overall.reservationId}`,
        guestUserId,
        hostEmail: '',
        guestEmail: '',
        guestName: '',
        guestInitials: '',
        rating: overall.score?.score ?? 0,
        text: overall.score.description,
        subScores: {
          cleanliness: findScore(SUB_SCORE_TOPIC_MAP.cleanliness) ?? fallback,
          communication: findScore(SUB_SCORE_TOPIC_MAP.communication) ?? fallback,
          rulesFollowed: findScore(SUB_SCORE_TOPIC_MAP.rulesFollowed) ?? fallback,
          careOfSite: findScore(SUB_SCORE_TOPIC_MAP.careOfSite) ?? fallback,
          punctuality: findScore(SUB_SCORE_TOPIC_MAP.punctuality) ?? fallback,
        },
        createdAt: overall.created ? new Date(overall.created * 1000).toISOString() : new Date().toISOString(),
      });
    }
    return reviews;
  }

  // ---- Submission (async — real HTTP) -------------------------------------

  async submitReal(params: ISubmitHostReviewParams): Promise<IHostReview> {
    const user = this.auth.currentUser;
    if (!user?.id) throw new Error('Must be signed in to submit a review');
    const creatorId = user.id;

    // Only the rating endpoint is used — the review text lives on the
    // overall-topic rating's score.description, alongside the sub-scores,
    // rather than going through a separate review endpoint. groupRows()
    // above already reads it back this same way. Visibility (hidden until
    // both sides have rated each other) and the mutual-reveal notification
    // are handled server-side once the overall rating lands — see
    // GuestRatingService.create()/rating.controller.ts's createGuest().
    const subScoreRatings: IGuestRating[] = SUB_SCORE_KEYS.map((key) => ({
      topic: SUB_SCORE_TOPIC_MAP[key],
      score: { score: params.subScores[key], description: '' },
      guestRatedId: params.guestUserId,
      reservationId: params.reservationId,
      creatorId,
      creator: { id: creatorId },
    }));
    const overallRating: IGuestRating = {
      topic: RatingType.overall,
      score: { score: Math.round(averageHostSubScores(params.subScores)), description: params.text },
      guestRatedId: params.guestUserId,
      reservationId: params.reservationId,
      creatorId,
      creator: { id: creatorId },
    };

    const created = await firstValueFrom(this.rating.createGuestRatings([...subScoreRatings, overallRating]));
    const overallResult = created.find((r) => r.topic === RatingType.overall) ?? overallRating;

    const review: IHostReview = {
      id: String(overallResult.id ?? ''),
      bookingId: `res-${params.reservationId}`,
      guestUserId: params.guestUserId,
      hostEmail: params.hostEmail,
      guestEmail: params.guestEmail,
      guestName: params.guestName,
      guestInitials: params.guestInitials,
      rating: averageHostSubScores(params.subScores),
      text: params.text,
      subScores: params.subScores,
      createdAt: new Date().toISOString(),
    };
    this.upsertLocal(review);
    return review;
  }

  private upsertLocal(review: IHostReview): void {
    const next = this._reviews$.value.filter((r) => r.bookingId !== review.bookingId);
    next.push(review);
    this._reviews$.next(next);
  }

  // ---- Public read API (sync — reads the in-memory cache) -----------------

  list(): IHostReview[] {
    return this._reviews$.value;
  }

  forBooking(bookingId: string): IHostReview | null {
    return this._reviews$.value.find((r) => r.bookingId === bookingId) ?? null;
  }

  forGuest(guestUserId: number): IHostReview[] {
    return this._reviews$.value
      .filter((r) => r.guestUserId === guestUserId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  /** Aggregated guest reputation across every revealed host-review of this
   *  guest. Returns count = 0 when the guest is brand-new (or not yet
   *  hydrated) — caller should render "New guest". */
  aggregateForGuest(guestUserId: number): { rating: number; count: number } {
    const all = this.forGuest(guestUserId);
    if (all.length === 0) return { rating: 0, count: 0 };
    const sum = all.reduce((acc, r) => acc + r.rating, 0);
    return { rating: +(sum / all.length).toFixed(2), count: all.length };
  }
}
